import { Decimal } from 'decimal.js'

import type { Instrument } from '@/types/generated/common/types'

/**
 * `qtyPrecision` is a literal quantity step increment (e.g. "0.001", "1"), not a
 * decimal-place count — every venue's backend producer (Deribit, Bybit, Derive,
 * Databento, and now Hyperliquid) emits it that way, mirroring `minTradeAmount`.
 * Resolve it here rather than at each call site so nothing re-derives this and
 * drifts from the convention (see reference_qty_precision_cross_venue_semantics
 * for the pre-unification history of this field).
 */
export function resolveQtyDecimalPlaces(qtyPrecision: number | undefined): number | undefined {
  if (qtyPrecision === undefined || !Number.isFinite(qtyPrecision) || qtyPrecision <= 0) {
    return undefined
  }
  return new Decimal(qtyPrecision).decimalPlaces()
}

/** Quantity step increment for numeric inputs (e.g. a NumericStepper). Falls back to 1 when qtyPrecision is missing/invalid. */
export function resolveQtyStep(qtyPrecision: number | string | undefined): number {
  const pp = typeof qtyPrecision === 'string' ? Number(qtyPrecision) : qtyPrecision
  if (pp === undefined || !Number.isFinite(pp) || pp <= 0) return 1
  return pp
}

/** Rounds a quantity to the instrument's real precision; passes the raw value through when precision is unknown. */
export function roundQuantityToPrecision(
  quantity: number,
  qtyPrecision: number | undefined
): string {
  if (quantity <= 0) return ''
  const decimalPlaces = resolveQtyDecimalPlaces(qtyPrecision)
  if (decimalPlaces === undefined) return String(quantity)
  return new Decimal(quantity).toDecimalPlaces(decimalPlaces).toString()
}

/**
 * Floors a shortcut quantity to an exact venue step before placing it in an input.
 * Flooring is intentional: a rounded-up Max value could exceed the available balance.
 */
export function floorQuantityToStep(
  quantity: string | number,
  qtyStep: number | undefined
): string {
  const normalized = String(quantity).replace(/,/g, '').trim()
  if (!normalized) return ''

  let value: Decimal
  try {
    value = new Decimal(normalized)
  } catch {
    return ''
  }

  if (!value.isFinite() || value.lte(0)) return ''
  if (qtyStep === undefined || !Number.isFinite(qtyStep) || qtyStep <= 0) {
    return value.toString()
  }

  const step = new Decimal(qtyStep)
  const floored = value.dividedBy(step).floor().times(step)
  if (floored.lte(0)) return ''

  return floored.toFixed(step.decimalPlaces())
}

/** Symbol → raw venue-native qtyPrecision map, built the same way by every table that needs one. */
export function buildQtyPrecisionMap(instruments: Iterable<Instrument>): Record<string, number> {
  const map: Record<string, number> = {}
  for (const inst of instruments) {
    const qp = Number(inst.qtyPrecision)
    if (!isNaN(qp)) map[inst.symbol] = qp
  }
  return map
}
